import { useHandler } from "@chatbotgang/etude/react/useHandler";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { Typography } from "@/components/Typography";
import { useExportBillingUsage } from "@/features/billing/useExportBillingUsage";

const MIN_MONTH = "2026-04";

const styles = {
  popoverPaper: {
    padding: "16px",
    minWidth: "280px",
    border: `1px solid ${theme.colors.neutral020}`,
  },
  monthInput: {
    width: "100%",
  },
  actions: {
    justifyContent: "flex-end",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const ExportUsageButton: React.FC = function ExportUsageButton() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const currentMonth = dayjs().format("YYYY-MM");
  const [month, setMonth] = useState(currentMonth);

  const exportMutation = useExportBillingUsage();

  const handleOpen = useHandler<React.MouseEventHandler<HTMLElement>>(
    (event) => {
      setMonth(currentMonth);
      setAnchorEl(event.currentTarget);
    },
  );

  const handleClose = useHandler(() => {
    setAnchorEl(null);
  });

  const handleMonthChange = useHandler<
    React.ComponentProps<typeof TextField>["onChange"]
  >((event) => {
    setMonth(event?.target.value ?? "");
  });

  const handleExport = useHandler(() => {
    exportMutation.mutate(
      { month },
      {
        onSuccess: () => {
          setAnchorEl(null);
        },
      },
    );
  });

  return (
    <>
      <Button variant="secondary" onClick={handleOpen}>
        {t("billing.export.button")}
      </Button>
      <Popover
        open={anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: styles.popoverPaper } }}
      >
        <Stack gap="12px">
          <Typography variant="h3" fontWeight={600}>
            {t("billing.export.popover.title")}
          </Typography>
          <Stack gap="4px">
            <Typography variant="note" color={theme.colors.neutral080}>
              {t("billing.export.popover.monthLabel")}
            </Typography>
            <TextField
              type="month"
              size="small"
              value={month}
              onChange={handleMonthChange}
              sx={styles.monthInput}
              slotProps={{
                htmlInput: {
                  min: MIN_MONTH,
                  max: currentMonth,
                },
              }}
            />
          </Stack>
          <Stack direction="row" gap="8px" sx={styles.actions}>
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={exportMutation.isPending}
            >
              {t("billing.export.popover.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              loading={exportMutation.isPending}
              disabled={!month || month < MIN_MONTH || month > currentMonth}
            >
              {t("billing.export.popover.action")}
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
